import { useTranslation } from 'react-i18next';
import { Badge, cn } from '@scio3/ui';
import { BILLING_INTERVAL, type BillingInterval } from '@scio3/shared-types';
import { formatCurrencyAmount } from '../utils/plan-helpers';

type BillingCycleToggleProps = {
  value: BillingInterval;
  onChange: (interval: BillingInterval) => void;
  monthlyPricePerScreen: number;
  yearlyPricePerScreen: number;
  /** Per-screen price for the "Yearly + Free Device" tier (SCIO 1.0 parity). */
  yearlyFreeDevicePricePerScreen?: number;
  screenCount: number;
  currency?: string;
  /** Render the 3rd "YEARLY + FREE DEVICE" card. SCIO 1.0 gate:
   *  `currency === 'usd' && company.feature.enableYearlyFreeDevice`. */
  showYearlyFreeDevice?: boolean;
  /** Grey out and block selection for the freedevice card
   *  (SCIO 1.0 subscriptionPlanModal.js isDisablePlan). */
  yearlyFreeDeviceDisabled?: boolean;
  /** Grey out the Monthly card (SCIO 1.0 isDisablePlan: set when the user
   *  already has `subscription_freedevice_id` and override flags don't apply). */
  monthlyDisabled?: boolean;
  /** Grey out the Yearly card. Same reason as above. */
  yearlyDisabled?: boolean;
};

/**
 * Payment term selection matching scio1's step 3 layout.
 * Two or three large side-by-side cards: Monthly, Yearly, and optionally
 * Yearly + Free Device. Selected card gets teal background.
 */
export function BillingCycleToggle({
  value,
  onChange,
  monthlyPricePerScreen,
  yearlyPricePerScreen,
  yearlyFreeDevicePricePerScreen = 0,
  currency = 'usd',
  showYearlyFreeDevice = false,
  yearlyFreeDeviceDisabled = false,
  monthlyDisabled = false,
  yearlyDisabled = false,
}: BillingCycleToggleProps) {
  const { t } = useTranslation();

  type Option = {
    id: BillingInterval;
    label: string;
    price: string;
    subtitle: string;
    discountText: string | null;
    note: string | null;
    disabled: boolean;
  };

  const options: Option[] = [
    {
      id: BILLING_INTERVAL.MONTHLY,
      label: t('MONTHLY'),
      price: formatCurrencyAmount(monthlyPricePerScreen, currency),
      subtitle: t('Per screen per month'),
      discountText: null,
      note: null,
      disabled: monthlyDisabled,
    },
    {
      id: BILLING_INTERVAL.YEARLY,
      label: t('YEARLY'),
      price: formatCurrencyAmount(yearlyPricePerScreen, currency),
      subtitle: t('Per screen per year'),
      discountText: t('10% off if paid yearly'),
      note: `*${t('No Free Devices')}`,
      disabled: yearlyDisabled,
    },
  ];

  if (showYearlyFreeDevice) {
    options.push({
      id: BILLING_INTERVAL.YEARLY_FREEDEVICE,
      label: t('YEARLY + FREE DEVICE'),
      price: formatCurrencyAmount(yearlyFreeDevicePricePerScreen, currency),
      subtitle: t('Per screen per year'),
      discountText: t('Includes a free device'),
      note: null,
      disabled: yearlyFreeDeviceDisabled,
    });
  }

  const gridCols = options.length === 3 ? 'grid-cols-3' : 'grid-cols-2';

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-semibold text-center">{t('Select Your Payment Term')}</h1>

      <div className={cn('grid gap-4', gridCols)}>
        {options.map((option) => {
          const isSelected = value === option.id;
          const isDisabled = option.disabled;
          return (
            <div
              key={option.id}
              aria-disabled={isDisabled || undefined}
              className={cn(
                'rounded-lg border-2 transition-colors overflow-hidden',
                isDisabled
                  ? 'border-border bg-muted/40 text-muted-foreground cursor-not-allowed opacity-60'
                  : isSelected
                    ? 'border-primary bg-primary text-white cursor-pointer'
                    : 'border-border bg-card hover:border-primary/50 cursor-pointer',
              )}
              onClick={() => {
                if (isDisabled) return;
                onChange(option.id);
              }}
            >
              <div className="p-6 text-center">
                {/* Title */}
                <h3
                  className={cn(
                    'text-sm font-bold tracking-wide mb-3',
                    isDisabled
                      ? 'text-muted-foreground'
                      : isSelected
                        ? 'text-white'
                        : 'text-foreground',
                  )}
                >
                  {option.label}
                </h3>

                <hr
                  className={cn(
                    'mb-4',
                    isSelected && !isDisabled ? 'border-white/30' : 'border-border',
                  )}
                />

                {/* Price */}
                <p
                  className={cn(
                    'text-3xl font-bold',
                    isDisabled
                      ? 'text-muted-foreground'
                      : isSelected
                        ? 'text-white'
                        : 'text-foreground',
                  )}
                >
                  {option.price}
                </p>

                {/* Subtitle */}
                <p
                  className={cn(
                    'text-sm mt-3',
                    isDisabled
                      ? 'text-muted-foreground'
                      : isSelected
                        ? 'text-white/80'
                        : 'text-muted-foreground',
                  )}
                >
                  {option.subtitle}
                </p>

                {/* Discount badge */}
                {option.discountText && (
                  <Badge
                    className={cn(
                      'mt-3 text-white',
                      isDisabled ? 'bg-muted hover:bg-muted' : 'bg-amber-500 hover:bg-amber-500',
                    )}
                  >
                    {option.discountText}
                  </Badge>
                )}

                {/* Note */}
                {option.note && (
                  <p
                    className={cn(
                      'text-xs mt-3',
                      isDisabled
                        ? 'text-muted-foreground'
                        : isSelected
                          ? 'text-white/60'
                          : 'text-muted-foreground',
                    )}
                  >
                    {option.note}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
